/**
 * DEFCAT epic (lyse@7.9.0) — TanStack Query hooks for defect COLLECTIONS
 * (per-user, intra-tenant subsets that narrow the drawer pickers) + the
 * cross-tenant SHARED catalogues list. Mirrors the Story-033 catalogue
 * query conventions (staleTime 5m / gcTime 10m / retry 1).
 *
 * SYNC: backend-requirement lyse-7.9.0-defcat.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@aeriosiq/api-client";
import type {
  CreateDefectCollectionBody,
  DefectCollection,
  ListDefectCollectionsResponse,
  ListSharedCataloguesResponse,
  SetActiveCollectionBody,
  SetActiveCollectionResponse,
  UpdateDefectCollectionBody,
} from "@aeriosiq/types";

const STALE_TIME_MS = 5 * 60_000;
const GC_TIME_MS = 10 * 60_000;

export const collectionKeys = {
  all: ["defect-collections"] as const,
  list: (catalogueId: string) =>
    [...collectionKeys.all, "list", catalogueId] as const,
  shared: ["shared-catalogues"] as const,
};

/** GET /defect-collections?catalogueId= — own + tenant-visible collections. */
export function useDefectCollectionsQuery(catalogueId: string | null) {
  const api = useApi();
  return useQuery<ListDefectCollectionsResponse>({
    queryKey: collectionKeys.list(catalogueId ?? ""),
    enabled: catalogueId !== null && catalogueId.length > 0,
    queryFn: async () => {
      const safeId = catalogueId ?? "";
      const res = await api.get<ListDefectCollectionsResponse>(
        `/defect-collections?catalogueId=${encodeURIComponent(safeId)}`,
      );
      return res.data;
    },
    staleTime: STALE_TIME_MS,
    gcTime: GC_TIME_MS,
    retry: 1,
    refetchOnWindowFocus: true,
    meta: { failureToast: "Collections unavailable" },
  });
}

/** GET /defect-catalogues/shared — catalogues other tenants granted to us. */
export function useSharedCataloguesQuery() {
  const api = useApi();
  return useQuery<ListSharedCataloguesResponse>({
    queryKey: collectionKeys.shared,
    queryFn: async () => {
      const res = await api.get<ListSharedCataloguesResponse>(
        "/defect-catalogues/shared",
      );
      return res.data;
    },
    staleTime: STALE_TIME_MS,
    gcTime: GC_TIME_MS,
    retry: 1,
    refetchOnWindowFocus: true,
    meta: { failureToast: "Shared catalogues unavailable" },
  });
}

export function useCreateCollectionMutation() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<DefectCollection, Error, CreateDefectCollectionBody>({
    mutationFn: async (body) => {
      const res = await api.post<DefectCollection>("/defect-collections", body);
      return res.data;
    },
    onSuccess: (created) => {
      void qc.invalidateQueries({
        queryKey: collectionKeys.list(created.catalogueId),
      });
    },
  });
}

export function useUpdateCollectionMutation(catalogueId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<
    DefectCollection,
    Error,
    { collectionId: string; body: UpdateDefectCollectionBody }
  >({
    mutationFn: async ({ collectionId, body }) => {
      const res = await api.patch<DefectCollection>(
        `/defect-collections/${encodeURIComponent(collectionId)}`,
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: collectionKeys.list(catalogueId) });
    },
  });
}

export function useDeleteCollectionMutation(catalogueId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (collectionId: string): Promise<void> => {
      await api.delete(
        `/defect-collections/${encodeURIComponent(collectionId)}`,
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: collectionKeys.list(catalogueId) });
    },
  });
}

export function useSetActiveCollectionMutation() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<
    SetActiveCollectionResponse,
    Error,
    SetActiveCollectionBody
  >({
    mutationFn: async (body) => {
      const res = await api.put<SetActiveCollectionResponse>(
        "/defect-collections/active",
        body,
      );
      return res.data;
    },
    onSuccess: (resp) => {
      void qc.invalidateQueries({
        queryKey: collectionKeys.list(resp.catalogueId),
      });
    },
  });
}
